import { TFunction } from 'i18next';
import { useFetchUserHasPermissions } from '../../api/user/useFetchUserHasPermissions';
import { MappedUser } from '../../api/user/user.mapper';
import appConfig from '../../configs/appConfig';
import { useFetchFeatureFlag } from '../../hooks/queries/useFetchFeatureFlag';
import { useOrganizationId } from '../../hooks/utils/useOrganizationId';
import { OrganizationSubscription } from '@manifest-cyber/types/interface/db';
import { hasOrganizationSubscriptions } from '../../lib/hasOrganizationSubscriptions';

export interface SubItem {
  id:
    | 'settings.menu.option.integrations'
    | 'settings.menu.option.labels'
    | 'settings.menu.option.licenses'
    | 'settings.menu.option.sharing-portal'
    | 'settings.menu.option.sbom-upload-alerts'
    | 'settings.menu.option.sla'
    | 'settings.menu.option.users'
    | 'settings.menu.option.suborganizations'
    | 'settings.menu.option.upload-settings'
    | 'settings.menu.option.roles'
    | 'settings.menu.option.api-tokens'
    | 'settings.menu.option.notifications'
    | 'settings.menu.option.feature-toggle'
    | 'settings.menu.option.profile'
    | 'settings.menu.option.vuln-alerts'
    | 'settings.menu.option.ai-policies'
    | 'settings.menu.option.ai-licenses';
  isVisible: boolean;
  label: string;
  path: string;
  requiredSubscriptions?: Array<keyof OrganizationSubscription>;
}

export interface NavItem {
  id:
    | 'settings.menu.section.organization'
    | 'settings.menu.section.membership'
    | 'settings.menu.section.account';
  title: string;
  isVisible: boolean;
  items: SubItem[];
}

export const useSettingsNavOptions = ({
  t,
  user,
  sbomUploadAlertsFlag,
  subscriptions,
}: {
  pathname: string;
  t: TFunction;
  sbomUploadAlertsFlag: boolean;
  user: MappedUser | undefined;
  currentOrgId?: string;
  subscriptions?: OrganizationSubscription;
}) => {
  const [currentOrgId] = useOrganizationId();
  const { data } = useFetchFeatureFlag({
    name: 'slaV1',
    organizationId: currentOrgId,
  });
  const showSla = Boolean(data?.isEnabled);
  const [organizationId] = useOrganizationId();
  const { data: vulnAlertsFeatureFlagData } = useFetchFeatureFlag({
    name: 'new-vuln-alerts-revamp',
    organizationId,
  });

  const canReadApiTokens = useFetchUserHasPermissions({
    requiredPermissions: ['read:api-token'],
  });

  const canReadAiPolicies = useFetchUserHasPermissions({
    requiredPermissions: ['read:ai-model-inventory'],
  });

  const canReadIntegration = useFetchUserHasPermissions({
    requiredPermissions: ['read:integration'],
  });

  const canReadLabel = useFetchUserHasPermissions({
    requiredPermissions: ['read:label'],
  });

  const canReadLicense = useFetchUserHasPermissions({
    requiredPermissions: ['read:license'],
  });

  const canUpdateUserVulnerabilityNotification = useFetchUserHasPermissions({
    requiredPermissions: [
      'read:user-vulnerability-notification',
      'update:user-vulnerability-notification',
    ],
  });

  const canReadSharingPortal = useFetchUserHasPermissions({
    requiredPermissions: ['read:sharing-portal'],
  });

  const canReadOrganizationAlert = useFetchUserHasPermissions({
    requiredPermissions: ['read:organization-alert'],
  });

  const canReadSla = useFetchUserHasPermissions({
    requiredPermissions: ['read:sla'],
  });

  const canReadEnrichmentSetting = useFetchUserHasPermissions({
    requiredPermissions: ['read:enrichment-setting'],
  });

  const hasPlatformAdminAccess = useFetchUserHasPermissions({
    requiredPermissions: [],
    availableOnlyForPlatformAdmins: true,
  });

  const canReadRole = useFetchUserHasPermissions({
    requiredPermissions: ['read:role'],
  });

  const canUpdateUser = useFetchUserHasPermissions({
    requiredPermissions: ['read:user', 'update:user', 'delete:user', 'create:user'],
  });

  const canReadSubOrganization = useFetchUserHasPermissions({
    requiredPermissions: ['read:sub-organization'],
  });

  const isParentOrganization = Boolean(user?.organization?.isParentOrg);

  const organizationSection: NavItem = {
    id: 'settings.menu.section.organization',
    title: t('global.organization'),
    isVisible: true,
    items: [
      {
        id: 'settings.menu.option.ai-policies',
        label: t('page.userSettings.navItems.aiPolicies'),
        isVisible: canReadAiPolicies,
        path: '/settings/ai-policies',
        requiredSubscriptions: ['aibom'],
      },
      {
        id: 'settings.menu.option.api-tokens',
        label: t('page.userSettings.navItems.apiTokens'),
        isVisible: canReadApiTokens,
        path: '/settings/apiTokens',
      },
      {
        id: 'settings.menu.option.integrations',
        label: t('page.userSettings.navItems.integrations'),
        isVisible: canReadIntegration,
        path: '/settings/integrations',
      },
      {
        id: 'settings.menu.option.labels',
        label: t('page.userSettings.navItems.labels'),
        isVisible: canReadLabel,
        path: '/settings/labels',
      },
      {
        id: 'settings.menu.option.licenses',
        label: t('page.userSettings.navItems.licenses'),
        isVisible: canReadLicense,
        path: '/settings/licenses',
        requiredSubscriptions: [],
      },
      {
        id: 'settings.menu.option.notifications',
        label: t('page.userSettings.navItems.notifications'),
        isVisible:
          appConfig.enableEmailFeatures &&
          canUpdateUserVulnerabilityNotification &&
          !(vulnAlertsFeatureFlagData?.isEnabled || false),
        path: '/settings/notifications',
      },
      {
        id: 'settings.menu.option.sharing-portal',
        label: 'Sharing Portal',
        isVisible: canReadSharingPortal,
        path: '/settings/sharing-portal',
      },
      {
        id: 'settings.menu.option.sbom-upload-alerts',
        label: t('page.userSettings.navItems.sbomUploadAlerts'),
        isVisible: sbomUploadAlertsFlag && canReadOrganizationAlert,
        path: '/settings/sbom-upload-alerts',
      },
      {
        id: 'settings.menu.option.sla',
        label: t('page.userSettings.navItems.sla'),
        isVisible: Boolean(showSla) && canReadSla,
        path: '/settings/sla',
      },
      {
        id: 'settings.menu.option.upload-settings',
        label: t('page.userSettings.navItems.uploadSettings'),
        isVisible: canReadEnrichmentSetting,
        path: '/settings/upload-settings',
      },
      {
        id: 'settings.menu.option.vuln-alerts',
        label: t('page.userSettings.navItems.vulnAlerts'),
        isVisible: vulnAlertsFeatureFlagData?.isEnabled || false,
        path: '/settings/vuln-alerts',
      },
      {
        id: 'settings.menu.option.feature-toggle',
        label: t('page.userSettings.navItems.feature-toggle'),
        isVisible: hasPlatformAdminAccess,
        path: '/settings/feature-toggle',
      },
    ],
  };

  const membershipSection: NavItem = {
    id: 'settings.menu.section.membership',
    title: 'Membership',
    isVisible: true,
    items: [
      {
        id: 'settings.menu.option.roles',
        label: 'Roles',
        isVisible: canReadRole,
        path: '/settings/roles',
      },
      {
        id: 'settings.menu.option.users',
        label: t('page.userSettings.navItems.users'),
        isVisible: canUpdateUser,
        path: '/settings/people',
      },
      {
        id: 'settings.menu.option.suborganizations',
        label: t('page.userSettings.navItems.suborganizations'),
        isVisible: isParentOrganization && canReadSubOrganization,
        path: '/settings/suborganizations',
      },
    ],
  };

  const accountSection: NavItem = {
    id: 'settings.menu.section.account',
    title: t('global.account'),
    isVisible: true,
    items: [
      {
        id: 'settings.menu.option.profile',
        label: t('page.userSettings.navItems.profile'),
        isVisible: true,
        path: '/settings/profile',
      },
    ],
  };
  const nav: NavItem[] = [organizationSection, membershipSection, accountSection];

  const filteredNav = nav
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        // Check if item has required subscriptions
        const hasRequiredSubscriptions =
          !item.requiredSubscriptions ||
          hasOrganizationSubscriptions(subscriptions, item.requiredSubscriptions);

        // Item is visible if it passes both the visibility check and the subscription check
        return item.isVisible && hasRequiredSubscriptions;
      }),
    }))
    .filter((section) => section.isVisible && section.items.length > 0);

  return filteredNav;
};
